import React, { useEffect } from 'react';
import { connect, useDispatch } from 'react-redux';
import { graphql, navigate } from 'gatsby';
import Auth from '../components/Auth';
import { authActions, userActions, api } from '@detox/actions';
import '../fragments/plan-detail';
import '../fragments/aem-components';
import { getBtlCampignID } from '../helpers/common';
import { getCookie } from '@lux/helpers';
import CONSTANT from '../constants/common';
import BTLOfferDetailPage from '../components/BTLOfferDetailPage';
import getAemComponentByName from '../helpers/getAemComponentByName';
import { setBTLCampignId } from '../reducers/promotions';
import { useABTesting } from '@wec-core/ab-testing';
import { AbFeatureNames } from '../constants/ab-feature-names';
import { navigation } from '../middlewares/navigation-constants';
import CONSTANTS from '../constants/common';

export const OfferPage = props => {
  const dispatch = useDispatch();
  const {
    data,
    authStatus,
    userInformation,
    setAuthStatus,
    resetUser,
    getUserInformation,
    promotions,
    auth,
    apigeeToken
  } = props;

  const { allAemPages } = data;
  const isUserInfoLoading = Boolean(userInformation?.loading);
  const siteConfigurations = getAemComponentByName(
    allAemPages,
    'SiteConfigurations'
  );
  const isWorryFreeEnabled =
    siteConfigurations && siteConfigurations.switches.DETOX_WORRY_FREE;

  const {
    isFeatureAllowed: [
      abTestingLineSelectionEnabled,
      abTestPxAddonsFeatureEnabled
    ],
    isPending: abTestApiPending,
    isSuccess: abTestApiSuccess
  } = useABTesting({
    featureName: [
      AbFeatureNames.BTL_LINE_SELECTION,
      AbFeatureNames.BTL_PX_ADDONS
    ]
  });

  const hasUserInfo = Boolean(userInformation);
  const isUserLoggedIn = Boolean(
    hasUserInfo && !userInformation?.anonymousUser
  );

  // Get the btlCampignId from URL and set to redux store
  const btlCampignId = getBtlCampignID();
  useEffect(() => {
    if (btlCampignId) {
      dispatch(setBTLCampignId(btlCampignId));
    } else {
      navigate(`/`, { replace: true });
    }
    return;
  }, [btlCampignId]);

  useEffect(() => {
    if (!authStatus && auth?.sessionToken) {
      setAuthStatus('WORRY_FREE_LOGIN');
    }
  }, [auth?.sessionToken]);

  useEffect(() => {
    if (apigeeToken) {
      const cleanWorryFree = api?.mcss?.helpers?.isWorryFreeLoggedIn();
      cleanWorryFree &&
        dispatch(
          authActions.getAuthToken({
            cleanWorryFree: true,
            cleanWorryFreeOnePassHeader: true
          })
        );
    }
  }, [apigeeToken]);

  /* Clear user data on first load. */
  useEffect(() => {
    if (!btlCampignId) return;
    resetUser();
    const uiamAccessToken = getCookie(CONSTANT.UIAM_ACCESS_TOKEN);
    if (uiamAccessToken && !isUserLoggedIn) {
      getUserInformation();
    }
  }, []);

  const isLoading = isUserInfoLoading;

  return (
    <>
      <Auth
        loading={isLoading}
        isAuthenticated={isUserLoggedIn}
        authStatus={authStatus}
        setAuthStatus={setAuthStatus}
        isWorryFreeEnabled={isWorryFreeEnabled}
        onePassTargetUrl={`${navigation.BTL_OFFER_PAGE}?${CONSTANTS.URL_PARAMS.BTL_CAMPAIGN_ID}=${btlCampignId}`}
      >
        {btlCampignId && (
          <BTLOfferDetailPage
            data={data}
            abTestingLineSelectionEnabled={abTestingLineSelectionEnabled}
            abTestPxAddonsFeatureEnabled={abTestPxAddonsFeatureEnabled}
            abTestApiPending={abTestApiPending}
            abTestApiSuccess={abTestApiSuccess}
          />
        )}
      </Auth>
    </>
  );
};

/* istanbul ignore next */
const mapStateToProps = state => {
  return {
    auth: state.auth,
    authStatus: state.auth.status,
    userInformation: state.user.information,
    userMyInfo: state.user.cis.information?.cusMyInfo,
    selectedPlan: state.plan.selectedPlan,
    userLoading: state.user?.loading,
    promotions: state.promotions,
    apigeeToken: state?.apigeeAuth?.products?.access_token
  };
};

/* istanbul ignore next */
const mapDispatchToProps = dispatch => {
  const { setAuthStatus } = authActions;
  const { getUserInformation, resetUser } = userActions;
  return {
    setAuthStatus: status => dispatch(setAuthStatus(status)),
    resetUser: () => dispatch(resetUser()),
    getUserInformation: options => dispatch(getUserInformation(options))
  };
};

export default connect(mapStateToProps, mapDispatchToProps)(OfferPage);

export const query = graphql`
  {
    allPlan {
      edges {
        node {
          ...PlanDetailFragment
        }
      }
    }
    allAemPages(filter: { pageUrl: { eq: "/eshop/admin.xjson" } }) {
      edges {
        node {
          title
          components {
            ...AemComponentsFragment
          }
        }
      }
    }
    allVariableConfig {
      edges {
        node {
          name
          value
        }
      }
    }
    allAddonPreTick {
      edges {
        node {
          boIds
          id
          phoneList
          phoneSpecific
          planIds
          segment
          productId
          spsIds
          variationIds
          transactionVariationIds {
            gaVariationIds
            portinVariationIds
            reconVariationIds
          }
          transactionBoIds {
            gaBoIds
            portinBoIds
            reconBoIds
          }
          transactionSpsIds {
            gaSpsIds
            portinSpsIds
            reconSpsIds
          }
          transactionSpecific
          type
        }
      }
    }
    allChooseSimOptions {
      edges {
        node {
          notAvailable
          footerNotAvailable
          simTitle
          simTypeId
          simDetails {
            transactionType
            descWithDevice
            descWithoutDevice
            footerWithDevice
            footerWithoutDevice
            simPrice
          }
        }
      }
    }
    allAddonGroup {
      edges {
        node {
          flowType
          displayName
          icon
          iconImage {
            src
          }
          setupFee
          shortDescription
          categoryId
          categorySequence
          type
        }
      }
    }
    allAddon {
      edges {
        node {
          flowType
          contract
          groupName
          id
          onetimePrice
          phoneList
          price
          productDescription
          productSpecContainmentID
          productTitle
          ranking
          salesRanking
          atomic
          type
          promotionText
          requiredDevice
          addonPopupTitle
          addonPopupMessage
          addonPopupButtonLabel
          categoryId
          categorySequence
        }
      }
    }
    addonSimOnlyPlan {
      data {
        addonId
        price
        quantity
        unit
      }
      sms {
        addonId
        price
        quantity
        unit
      }
      talktime {
        addonId
        price
        quantity
        unit
      }
    }
    allPhone(filter: { brand: { eq: "Apple" } }) {
      edges {
        node {
          id
          groupId
          productId
          variants {
            productId
          }
        }
      }
    }
    allVariableConfig {
      edges {
        node {
          name
          value
        }
      }
    }
  }
`;
